/**
 * Utility per la validazione formale e il rilevamento refusi negli indirizzi email.
 * Verifica sintassi e controlla errori di digitazione sui provider di posta più comuni.
 */

export const KNOWN_DOMAINS = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'libero.it',
  'virgilio.it',
  'alice.it',
  'tim.it',
  'tiscali.it',
  'fastwebnet.it',
  'hotmail.it',
  'yahoo.it',
  'outlook.it',
];

export const COMMON_TLD_TYPOS = {
  cmo: 'com',
  con: 'com',
  coom: 'com',
  comm: 'com',
  ti: 'it',
  ne: 'net',
};

/**
 * Calcola la distanza di Levenshtein tra due stringhe.
 */
export function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (!a || a.length === 0) return b ? b.length : 0;
  if (!b || b.length === 0) return a ? a.length : 0;

  const aLen = a.length;
  const bLen = b.length;
  const matrix = Array.from({ length: bLen + 1 }, () => new Array(aLen + 1));

  for (let i = 0; i <= bLen; i++) matrix[i][0] = i;
  for (let j = 0; j <= aLen; j++) matrix[0][j] = j;

  for (let i = 1; i <= bLen; i++) {
    for (let j = 1; j <= aLen; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // sostituzione
          matrix[i][j - 1] + 1,     // inserimento
          matrix[i - 1][j] + 1      // cancellazione
        );
      }
    }
  }

  return matrix[bLen][aLen];
}

/**
 * Regex formale per la validazione sintattica dell'email.
 */
const EMAIL_SYNTAX_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Valida un indirizzo email e rileva potenziali errori formali o refusi di dominio.
 * 
 * @param {string} email - Indirizzo email da validare
 * @returns {{
 *   isValid: boolean,
 *   isSuspicious: boolean,
 *   warningMessage: string | null,
 *   suggestedEmail: string | null,
 *   suggestion: string | null
 * }}
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string' || !email.trim()) {
    return {
      isValid: true,
      isSuspicious: false,
      warningMessage: null,
      suggestedEmail: null,
      suggestion: null,
    };
  }

  const trimmed = email.trim();

  // Controllo sintattico di base
  const atCount = (trimmed.match(/@/g) || []).length;
  if (atCount !== 1) {
    return {
      isValid: false,
      isSuspicious: true,
      warningMessage: "Il formato dell'indirizzo email non sembra valido (manca il carattere '@' o ne sono presenti molteplici).",
      suggestedEmail: null,
      suggestion: null,
    };
  }

  if (trimmed.includes('..') || trimmed.includes(' ') || trimmed.includes(',') || trimmed.includes(';')) {
    return {
      isValid: false,
      isSuspicious: true,
      warningMessage: "L'indirizzo email contiene caratteri non validi (spazi, virgole o punti consecutivi).",
      suggestedEmail: null,
      suggestion: null,
    };
  }

  if (!EMAIL_SYNTAX_REGEX.test(trimmed)) {
    return {
      isValid: false,
      isSuspicious: true,
      warningMessage: "Il formato dell'indirizzo email non sembra valido (es. nome@dominio.it).",
      suggestedEmail: null,
      suggestion: null,
    };
  }

  const [localPart, domainPartRaw] = trimmed.split('@');
  const domainPart = domainPartRaw.toLowerCase();

  // Controllo caso specifico @gmail.it (Google non supporta il TLD .it per account Gmail)
  if (domainPart === 'gmail.it') {
    const suggestedEmail = `${localPart}@gmail.com`;
    return {
      isValid: true,
      isSuspicious: true,
      warningMessage: `L'indirizzo email sembra contenere un errore di digitazione. Forse intendevi ${suggestedEmail}?`,
      suggestedEmail,
      suggestion: suggestedEmail,
    };
  }

  // Controllo TLD errati comuni (.cmo, .con, .ti, ecc.)
  const domainSegments = domainPart.split('.');
  if (domainSegments.length >= 2) {
    const tld = domainSegments[domainSegments.length - 1];
    if (COMMON_TLD_TYPOS[tld]) {
      const correctedTld = COMMON_TLD_TYPOS[tld];
      domainSegments[domainSegments.length - 1] = correctedTld;
      const suggestedDomain = domainSegments.join('.');
      const suggestedEmail = `${localPart}@${suggestedDomain}`;
      return {
        isValid: true,
        isSuspicious: true,
        warningMessage: `L'estensione dell'indirizzo email (.${tld}) sembra errata. Forse intendevi ${suggestedEmail}?`,
        suggestedEmail,
        suggestion: suggestedEmail,
      };
    }
  }

  // Se il dominio fa già parte della lista dei domini noti, è sicuramente valido
  if (KNOWN_DOMAINS.includes(domainPart)) {
    return {
      isValid: true,
      isSuspicious: false,
      warningMessage: null,
      suggestedEmail: null,
      suggestion: null,
    };
  }

  // Controllo somiglianza (Levenshtein) con i domini più noti
  let bestMatch = null;
  let minDistance = Infinity;

  for (const known of KNOWN_DOMAINS) {
    const maxAllowedDistance = known.length <= 6 ? 1 : 2;
    const distance = levenshteinDistance(domainPart, known);

    if (distance > 0 && distance <= maxAllowedDistance && distance < minDistance) {
      minDistance = distance;
      bestMatch = known;
    }
  }

  if (bestMatch) {
    const suggestedEmail = `${localPart}@${bestMatch}`;
    return {
      isValid: true,
      isSuspicious: true,
      warningMessage: `L'indirizzo email sembra contenere un errore di digitazione. Forse intendevi ${suggestedEmail}?`,
      suggestedEmail,
      suggestion: suggestedEmail,
    };
  }

  // Nessuna anomalia rilevata
  return {
    isValid: true,
    isSuspicious: false,
    warningMessage: null,
    suggestedEmail: null,
    suggestion: null,
  };
}

/**
 * Controlla una lista di campi email e restituisce la lista di avvisi (se presenti).
 * 
 * @param {Array<{ label: string, value: string, fieldId?: string, selector?: string }>} fields
 * @returns {Array<{ label: string, email: string, warningMessage: string, suggestedEmail?: string, fieldId?: string, selector?: string }>}
 */
export function checkEmailsForWarnings(fields = []) {
  const warnings = [];

  for (const field of fields) {
    if (!field || !field.value || !field.value.trim()) continue;

    const validation = validateEmail(field.value);
    if (validation.isSuspicious) {
      warnings.push({
        label: field.label || 'Email',
        email: field.value.trim(),
        warningMessage: validation.warningMessage,
        suggestedEmail: validation.suggestedEmail,
        fieldId: field.fieldId,
        selector: field.selector,
      });
    }
  }

  return warnings;
}
